import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, CheckCircle2, Zap } from 'lucide-react';
import apiClient from '@/api';
import Navbar from '@/components/Navbar';

const AVATAR_EMOJIS = ['🌱', '🌿', '🪴', '🌸', '🌺', '🌻', '✨', '🌟'];

export default function Leveling() {
  const [levels, setLevels] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [levelsRes, progressRes] = await Promise.all([
          apiClient.get('/levels'),
          apiClient.get('/my/progress'),
        ]);
        setLevels(levelsRes.data);
        setProgress(progressRes.data);
      } catch (error) {
        console.error('Failed to fetch level system:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center font-black text-[#D4A574]">
        LOADING LEVEL SYSTEM...
      </div>
    );
  }

  const currentLevel = progress?.level || 1;
  const currentXp = progress?.xp || 0;

  return (
    <div className="min-h-screen bg-[#F5F3EF] text-[#5C4A42]">
      <Navbar />
      <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">

        <header>
          <h1 className="text-4xl md:text-6xl font-black uppercase italic tracking-tighter">
            Level <span className="text-[#D4A574]">Roadmap</span>
          </h1>
          <p className="text-[#8B7355] font-mono text-[10px] uppercase tracking-[0.2em] mt-2">
            // Every tier ahead, and what it unlocks
          </p>
        </header>

        <div className="space-y-4">
          {levels.map((level, idx) => {
            const isCompleted = currentLevel > level.level;
            const isCurrent = currentLevel === level.level;
            const isLocked = currentLevel < level.level;

            return (
              <motion.div
                key={level.level}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`warm-card p-6 rounded-2xl flex items-center gap-6 ${
                  isCurrent ? 'ring-2 ring-[#D4A574]' : ''
                } ${isLocked ? 'opacity-60' : ''}`}
              >
                <div className="text-5xl shrink-0">
                  {AVATAR_EMOJIS[level.avatar_stage - 1] || '🌱'}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-black uppercase italic">
                      LV {level.level} &middot; {level.name}
                    </h2>
                    {isCompleted && <CheckCircle2 className="w-5 h-5 text-[#D4A574]" />}
                    {isLocked && <Lock className="w-4 h-4 text-[#A89284]" />}
                    {isCurrent && (
                      <span className="text-[10px] font-black uppercase tracking-widest bg-[#D4A574] text-white px-2 py-0.5 rounded-full">
                        You are here
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#8B7355] mt-1">
                    {level.xp_required.toLocaleString()} XP required
                    {level.points_reward > 0 && ` • +${level.points_reward} pts on reaching this level`}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="warm-card p-6 rounded-2xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="text-[#D4A574]" size={18} />
            <span className="text-sm font-bold uppercase text-[#8B7355]">Your current XP</span>
          </div>
          <span className="text-2xl font-black italic">{currentXp.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}
